import React, { ReactNode, useState } from 'react';
import { projectApi } from '../../../../../services/projectApi';
import { Button, ClickAwayListener, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Folder as ProjectIcon } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import ProjectSelectionDialog from './ProjectSelectionDialog';
import { Project } from '../../../../../types/project';

type ProjectSelectorProps = {
	icon?: ReactNode;
	selectProject: (project: Project | undefined) => void;
	buttonColor?: any;
	companyId: string;
};

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ selectProject, icon, buttonColor, companyId }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const { data: timelines } = projectApi.useFetchTimelineQuery({ companyId }, { skip: !companyId });
	const [selectedTimelineId, setSelectedTimelineId] = useState<string | null>(null);

	React.useEffect(() => {
		if (timelines?.length > 0 && !selectedTimelineId) {
			setSelectedTimelineId(timelines[0].timelineId);
		}
	}, [timelines, selectedTimelineId]);

	const { data: projects } = projectApi.useFetchProjectsQuery(
		{ companyId, timelineId: selectedTimelineId as string },
		{ skip: !selectedTimelineId || !companyId },
	);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSelectProject = (project: Project) => {
		selectProject(project);
		handleClose();
	};

	const open = Boolean(anchorEl);

	return (
		<ClickAwayListener onClickAway={handleClose}>
			<div style={{ position: 'relative', zIndex: 9999 }}>
				{!projects || projects?.length === 0 ? (
					<Tooltip title={<FormattedMessage id='companyNotes.selectCompanyFirst' defaultMessage='Select a company first' />}>
						<span>
							<Button
								disabled={true}
								onClick={handleClick}
								variant='text'
								startIcon={open ? <CloseIcon /> : <ProjectIcon />}
								aria-expanded={Boolean(anchorEl)}
								aria-haspopup='true'
								color={buttonColor}
							>
								<FormattedMessage id='projectSelector.selectProject' />
							</Button>
						</span>
					</Tooltip>
				) : (
					<Button
						onClick={handleClick}
						variant='text'
						startIcon={open ? <CloseIcon /> : <ProjectIcon />}
						aria-expanded={Boolean(anchorEl)}
						aria-haspopup='true'
						color={buttonColor}
					>
						<FormattedMessage id='projectSelector.selectProject' />
					</Button>
				)}
				<ProjectSelectionDialog
					open={open}
					onClose={handleClose}
					onSelect={handleSelectProject}
					anchorEl={anchorEl}
					projects={projects || []}
				/>
			</div>
		</ClickAwayListener>
	);
};

export default ProjectSelector;
